import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { agencyListAlerts, agencyLogout, agencySetOperationalStatus } from "../lib/api";
import { agencyAuth } from "../lib/identity";
import { useAgencyAuth } from "../lib/useIdentity";
import { useAgencyFirehose } from "../lib/useAgencyFirehose";
import { Building2, LogOut, MapPin, RefreshCw, Truck, CheckCircle2, ExternalLink, Bell, BellOff, Wifi, WifiOff, Phone, AlertTriangle, Activity, Inbox, Navigation2 } from "lucide-react";
import { AgencyCaseFilePanel } from "../components/AgencyCaseFilePanel";
import { toast } from "sonner";
import { MaliStripe } from "../components/MaliStripe";
import { SovereigntyBanner } from "../components/SovereigntyBanner";
import { useT } from "../lib/lang";
import { useCountryConfig } from "../lib/useCountryConfig";
import { dispatchSound } from "../lib/dispatchSound";
import { vibrate } from "../lib/silent";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch (_e) { return iso; }
};

const fmtHM = (date) => {
  try { return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch (_e) { return ""; }
};

// Iter65 — operational lifecycle owned by the assigned agency after
// Central-Admin dispatch. Five primary statuses form the normal flow;
// `Besoin d'escalade` is an internal-only escalation that we surface
// SEPARATELY (smaller, secondary styling) so it doesn't visually
// compete with the standard progression. The `citizen` hint shows what
// the reporting citizen will see in their public-facing status page.
const PRIMARY_OP_STATUSES = [
  { label: "Reçue par l'Agence",      tone: "sky",     icon: Inbox,    citizen: "Alerte reçue" },
  { label: "En traitement",           tone: "amber",   icon: Activity, citizen: "En cours de traitement" },
  { label: "Service contacté",        tone: "violet",  icon: Phone,    citizen: "En cours de traitement" },
  { label: "En cours d'intervention", tone: "emerald", icon: Truck,    citizen: "Intervention en cours" },
  { label: "Résolue",                 tone: "ink",     icon: CheckCircle2, citizen: "Résolue" },
];
const ESCALATION_STATUS = {
  label: "Besoin d'escalade",
  tone: "rose",
  icon: AlertTriangle,
  citizen: null, // never shown to citizen
};
const ALL_OP_STATUSES = [...PRIMARY_OP_STATUSES, ESCALATION_STATUS];
const OP_META_BY_LABEL = Object.fromEntries(ALL_OP_STATUSES.map((s) => [s.label, s]));

const opToneClasses = (tone, active) => ({
  sky:     active ? "bg-sky-500 text-zinc-950 border-sky-500"           : "border-sky-500/50 text-sky-300 hover:bg-sky-500/10",
  amber:   active ? "bg-amber-500 text-zinc-950 border-amber-500"       : "border-amber-500/50 text-amber-300 hover:bg-amber-500/10",
  violet:  active ? "bg-violet-500 text-zinc-950 border-violet-500"     : "border-violet-500/50 text-violet-300 hover:bg-violet-500/10",
  emerald: active ? "bg-emerald-500 text-zinc-950 border-emerald-500"   : "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10",
  rose:    active ? "bg-rose-500 text-white border-rose-500"            : "border-rose-500/50 text-rose-300 hover:bg-rose-500/10",
  ink:     active ? "bg-zinc-100 text-zinc-950 border-zinc-100"         : "border-zinc-500/60 text-zinc-200 hover:bg-zinc-100 hover:text-zinc-950",
}[tone] || "");

/**
 * Iter65 — single source of truth for post-dispatch status updates.
 * Renders the 5 primary operational chips as a clear left-to-right
 * progression and surfaces `Besoin d'escalade` separately as a
 * secondary, smaller button so it never competes with normal flow.
 * Hits ``/agency/alerts/{id}/operational-status``, which the backend
 * gates on ``current_assigned_agence_ids``. The legacy
 * `/intervention` buttons + duplicate note textarea were removed in
 * this iteration.
 */
function AgencyOpStatusPanel({ alert, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const current = alert.operational_status;
  const currentMeta = current ? OP_META_BY_LABEL[current] : null;

  const submit = async (label) => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await agencySetOperationalStatus(alert.id, label, note.trim() || null);
      toast.success(`Statut opérationnel: ${label}`);
      setNote("");
      onUpdate?.(updated);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Mise à jour impossible";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="mt-3 border border-emerald-500/30 bg-emerald-500/5 p-2 space-y-2"
      data-testid={`agency-op-panel-${alert.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-300 flex items-center gap-1">
          <Activity className="w-3 h-3" /> Statut opérationnel
        </span>
        {currentMeta ? (
          <span
            data-testid={`agency-op-current-${alert.id}`}
            className={`text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border ${opToneClasses(currentMeta.tone, true)}`}
          >
            {current}
          </span>
        ) : (
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-500 italic">
            Non défini
          </span>
        )}
      </div>
      <textarea
        data-testid={`agency-op-note-${alert.id}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Ajouter une note opérationnelle pour cette mise à jour"
        rows={2}
        className="w-full border border-zinc-700 bg-zinc-900 text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none px-2 py-1.5 text-[11px] resize-none"
      />
      {/* Primary lifecycle — five linear stages. Click directly to set. */}
      <div className="grid grid-cols-2 gap-1.5">
        {PRIMARY_OP_STATUSES.map(({ label, tone, icon: Icon, citizen }) => {
          const active = current === label;
          return (
            <button
              key={label}
              type="button"
              data-testid={`agency-op-${label}`}
              onClick={() => submit(label)}
              disabled={busy || active}
              title={citizen ? `Citoyen verra: « ${citizen} »` : "Interne uniquement"}
              className={`py-1.5 px-2 text-[9px] font-bold uppercase tracking-[0.1em] border transition-colors disabled:opacity-50 flex items-center justify-center gap-1 ${opToneClasses(tone, active)}`}
            >
              <Icon className="w-3 h-3" /> {label}
            </button>
          );
        })}
      </div>
      {/* Escalation — kept separate (smaller, dashed) so it doesn't
          compete with the normal progression. Internal-only: the
          citizen sees no change when this is set. */}
      <button
        type="button"
        data-testid={`agency-op-${ESCALATION_STATUS.label}`}
        onClick={() => submit(ESCALATION_STATUS.label)}
        disabled={busy || current === ESCALATION_STATUS.label}
        title="Interne uniquement — citoyen ne voit aucun changement"
        className={`w-full py-1 px-2 text-[9px] font-bold uppercase tracking-[0.1em] border border-dashed transition-colors disabled:opacity-50 flex items-center justify-center gap-1 ${
          current === ESCALATION_STATUS.label
            ? "bg-rose-500 text-white border-rose-500 border-solid"
            : "border-rose-500/40 text-rose-300/90 hover:bg-rose-500/10 hover:border-rose-500/70"
        }`}
      >
        <AlertTriangle className="w-3 h-3" /> {ESCALATION_STATUS.label}
        <span className="text-[8px] font-normal opacity-70 normal-case tracking-normal">· interne</span>
      </button>
    </div>
  );
}

function StatusPill({ status }) {
  // Mirror the operational status as a small badge on the card header.
  // Uses the same tone as the OP panel so the card colour-code is
  // consistent at a glance.
  const meta = OP_META_BY_LABEL[status];
  if (!meta) {
    return <span className="px-2 py-1 text-[9px] font-bold uppercase tracking-[0.2em] bg-zinc-700 text-zinc-100">Reçue</span>;
  }
  return (
    <span className={`px-2 py-1 text-[9px] font-bold uppercase tracking-[0.2em] border ${opToneClasses(meta.tone, true)}`}>
      {status}
    </span>
  );
}

function AlertCard({ alert, caseFileRefreshKey = 0, onUpdate }) {
  const status = alert.operational_status || "Reçue par l'Agence";
  const c = alert.coordinates;
  const isClosed = status === "Résolue" || alert.intervention?.status === "closed" || alert.intervention?.status === "cancelled";
  const cfg = useCountryConfig();
  const brand = cfg?.brand_name || "SOS Mali";

  return (
    <div className="border border-zinc-800 bg-[#0f1318] p-3" data-testid={`agency-alert-${alert.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-[11px] text-zinc-400">
            {c.latitude.toFixed(4)}, {c.longitude.toFixed(4)}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {fmt(alert.created_at)} · <span className="font-mono">{alert.reporter_phone || "anon"}</span>
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      {alert.description && (
        <p className="text-xs text-zinc-200 mt-2 line-clamp-3">{alert.description}</p>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {/* Iter67 — Primary action: internal SOS Mali map (MapLibre +
            TomTom turn-by-turn). The /agence/carte route is gated by
            the same `_check_agency_assigned` server-side; an
            unassigned agency hitting the URL directly gets a 403. */}
        <a
          href={`/agence/carte?alert_id=${alert.id}`}
          data-testid={`agency-open-sos-map-${alert.id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
        >
          <Navigation2 className="w-3 h-3" /> Itinéraire {brand}
        </a>
        {/* Secondary fallback — kept ONLY for offline / no-session
            edge cases. Visually subordinate per spec §9. */}
        <a
          href={alert.map_link}
          target="_blank"
          rel="noreferrer"
          data-testid={`agency-open-map-${alert.id}`}
          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-200"
          title="Ouvrir dans une application externe"
        >
          <ExternalLink className="w-3 h-3" /> Externe
        </a>
      </div>

      {/* Iter56 Phase 3 — secure scoped case-file (collapsible).
          Lazy-loads on expand so dashboard list rendering stays fast.
          Iter66 — `caseFileRefreshKey` is bumped by the parent on each
          incoming `alert.citizen_amended` WS event so the panel
          re-mounts and refetches its amendment list. */}
      {!isClosed && (
        <AgencyCaseFilePanel key={`cf-${alert.id}-${caseFileRefreshKey}`} alertId={alert.id} />
      )}

      {/* Iter65 — operational status is now the SINGLE source of truth.
          The legacy intervention block (Accepter / En route / Sur place
          / Clôturer + duplicate Note opérationnelle) was removed in
          this iteration: every state it represented is covered by the
          5-stage operational lifecycle below. */}
      {!isClosed && (
        <AgencyOpStatusPanel alert={alert} onUpdate={onUpdate} />
      )}
    </div>
  );
}

const SOUND_KEY = "rr_agency_sound";
const SEEN_KEY_PREFIX = "rr_agency_seen_"; // + agency_id

const readBool = (k, fallback = true) => {
  try {
    const v = localStorage.getItem(k);
    if (v === null || v === undefined) return fallback;
    return v === "1";
  } catch (_e) { return fallback; }
};

export default function AgencyDashboardPage() {
  const [t] = useT();
  const [session] = useAgencyAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open"); // "open" | "all"
  const [soundOn, setSoundOn] = useState(() => readBool(SOUND_KEY, true));
  const [audioReady, setAudioReady] = useState(() => dispatchSound.isUnlocked());
  const [newCount, setNewCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshState, setRefreshState] = useState({ ok: null, at: null }); // {ok:true|false, at:Date}
  const seenRef = useRef(null); // Set<id> of alerts already known to this operator

  const ag = session || {};
  const seenStorageKey = ag.id ? `${SEEN_KEY_PREFIX}${ag.id}` : null;

  const persistSeen = useCallback((set) => {
    if (!seenStorageKey) return;
    try { localStorage.setItem(seenStorageKey, JSON.stringify([...set])); } catch (e) { /* noop */ void e; }
  }, [seenStorageKey]);

  // Background refresh — used by the 60 s safety-net poll AND the WS
  // first hydration. Does NOT touch the user-visible refresh state
  // (refreshing / refreshState) so the operator never sees "Mis à
  // jour à 12:34" flash from the automatic poll.
  const refresh = useCallback(async () => {
    try {
      const resp = await agencyListAlerts({ limit: 100 });
      const a = resp.items || [];
      let fresh = 0;
      if (seenRef.current) {
        const seen = seenRef.current;
        for (const al of a) {
          if (!seen.has(al.id)) {
            const s = al.intervention?.status;
            if (s !== "closed" && s !== "cancelled") fresh += 1;
            seen.add(al.id);
          }
        }
        if (fresh > 0) {
          persistSeen(seen);
          setNewCount((n) => n + fresh);
          if (soundOn) {
            dispatchSound.play();
            vibrate([180, 80, 180]);
            toast.info(
              fresh === 1 ? "Nouvelle alerte reçue" : `${fresh} nouvelles alertes reçues`,
              { id: "agency-new-alerts" }
            );
          }
        }
      } else {
        // First fetch: hydrate from localStorage so reload doesn't re-ring
        let stored = [];
        if (seenStorageKey) {
          try { stored = JSON.parse(localStorage.getItem(seenStorageKey) || "[]"); } catch (e) { /* noop */ void e; }
        }
        const set = new Set(stored);
        for (const al of a) set.add(al.id);
        seenRef.current = set;
        persistSeen(set);
      }
      // Replace only AFTER the fetch returned — never blank the list
      // while loading (acceptance §5: do not clear existing alerts
      // during refresh).
      setAlerts(a);
      return { ok: true };
    } catch (e) {
      if (e?.response?.status === 401) {
        agencyAuth.clear();
      }
      return { ok: false, error: e };
    } finally {
      setLoading(false);
    }
  }, [soundOn, persistSeen, seenStorageKey]);

  // Manual refresh — surfaces loading / success / failure feedback to
  // the operator per the Iter65 spec.
  const manualRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    const res = await refresh();
    setRefreshing(false);
    if (res.ok) {
      setRefreshState({ ok: true, at: new Date() });
    } else {
      setRefreshState({ ok: false, at: new Date() });
      toast.error("Échec de l'actualisation", { id: "agency-refresh" });
    }
  }, [refresh, refreshing]);

  useEffect(() => {
    if (!session) return undefined;
    seenRef.current = null; // re-init for this session
    // Defer state updates + first fetch out of the effect body so the
    // hooks-rules "set-state-in-effect" check stays happy.
    Promise.resolve().then(() => {
      setNewCount(0);
      refresh();
    });
    // Safety-net poll every 60 s in case the WS is wedged behind a
    // proxy. Real-time updates ride the firehose below; the slow poll
    // catches missed events and corrects any drift.
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [session?.id, refresh]);

  // Real-time firehose (Iter65) — new alerts (broad), targeted dispatch
  // events, and operational status changes are all pushed in real time
  // via the agency:<type> + agency-id:<uuid> rooms. The 60 s safety-net
  // poll above stays as a fallback if the WS is wedged behind a proxy.
  const handleAlertCreated = useCallback((alert) => {
    setAlerts((prev) => (prev.some((a) => a.id === alert.id) ? prev : [alert, ...prev]));
    const seen = seenRef.current;
    if (seen && !seen.has(alert.id)) {
      seen.add(alert.id);
      persistSeen(seen);
      const s = alert.intervention?.status;
      if (s !== "closed" && s !== "cancelled") {
        setNewCount((n) => n + 1);
        if (soundOn) {
          dispatchSound.play();
          vibrate([180, 80, 180]);
          toast.info("Nouvelle alerte reçue", { id: "agency-new-alerts" });
        }
      }
    }
  }, [persistSeen, soundOn]);

  // Targeted dispatch from Central Admin — this is the event that makes
  // newly dispatched alerts appear INSTANTLY on the assigned agency
  // dashboard (previously they waited ≤ 60 s for the polling fallback).
  const handleAlertDispatched = useCallback((alert) => {
    setAlerts((prev) => {
      const idx = prev.findIndex((a) => a.id === alert.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = alert;
        return next;
      }
      return [alert, ...prev];
    });
    const seen = seenRef.current;
    if (seen && !seen.has(alert.id)) {
      seen.add(alert.id);
      persistSeen(seen);
      setNewCount((n) => n + 1);
      if (soundOn) {
        dispatchSound.play();
        vibrate([180, 80, 180]);
        toast.info("Alerte dispatchée à votre agence", { id: "agency-new-alerts" });
      }
    }
  }, [persistSeen, soundOn]);

  // Operational status update (this dispatcher's tab OR a teammate's).
  const handleAlertOperational = useCallback((alert) => {
    setAlerts((prev) => prev.map((a) => (a.id === alert.id ? alert : a)));
  }, []);

  const handleInterventionUpdate = useCallback((alertId, intervention) => {
    setAlerts((prev) => prev.map((a) => (a.id === alertId ? { ...a, intervention } : a)));
  }, []);

  // Iter90 — GPS audit: post-SOS location refinement for an alert this
  // agency can see. Patches coordinates/quality/freshness in place —
  // the map marker on AgencyMapPage (fed by this same `alerts` state)
  // moves without a refresh.
  const handleLocationUpdated = useCallback((alertId, msg) => {
    setAlerts((prev) => prev.map((a) => (a.id === alertId ? {
      ...a,
      coordinates: msg.coordinates || a.coordinates,
      location_quality: msg.location_quality,
      location_updated_at: msg.location_updated_at,
    } : a)));
  }, []);

  // Iter66 — Citizen submitted an amendment to an assigned alert. We
  // surface a toast + bump a per-alert refresh counter so the open
  // case-file panel (if any) re-fetches its amendment list.
  const [caseFileRefresh, setCaseFileRefresh] = useState({});
  const handleCitizenAmended = useCallback((alertId, _amendment) => {
    setCaseFileRefresh((m) => ({ ...m, [alertId]: (m[alertId] || 0) + 1 }));
    if (soundOn) {
      dispatchSound.play();
      vibrate([120, 60, 120]);
    }
    toast.info("Nouvelle mise à jour citoyen", { id: "agency-amend-" + alertId });
  }, [soundOn]);

  const { live: wsLive, connecting: wsConnecting } = useAgencyFirehose({
    enabled: !!session,
    onAlertCreated: handleAlertCreated,
    onAlertDispatched: handleAlertDispatched,
    onAlertOperational: handleAlertOperational,
    onInterventionUpdate: handleInterventionUpdate,
    onCitizenAmended: handleCitizenAmended,
    onLocationUpdated: handleLocationUpdated,
  });

  const visible = useMemo(() => {
    if (filter === "all") return alerts;
    return alerts.filter((a) => {
      const op = a.operational_status;
      const iv = a.intervention?.status;
      const isResolved = op === "Résolue" || iv === "closed" || iv === "cancelled";
      return !isResolved;
    });
  }, [alerts, filter]);

  const openCount = useMemo(() => alerts.filter((a) => {
    const op = a.operational_status;
    const iv = a.intervention?.status;
    return !(op === "Résolue" || iv === "closed" || iv === "cancelled");
  }).length, [alerts]);

  if (!session) return <Navigate to="/admin/agency/login" replace />;

  const handleLogout = async () => {
    await agencyLogout();
    agencyAuth.clear();
  };

  const toggleSound = async () => {
    const next = !soundOn;
    if (next && !audioReady) {
      const ok = await dispatchSound.unlock();
      setAudioReady(ok);
      if (ok) dispatchSound.play(); // confirmation chirp on first enable
    }
    setSoundOn(next);
    try { localStorage.setItem(SOUND_KEY, next ? "1" : "0"); } catch (e) { /* noop */ void e; }
  };

  const clearNewBadge = () => setNewCount(0);

  return (
    <div className="flex-1 flex flex-col bg-[#0a0d12] text-zinc-200 min-h-[100dvh]" data-testid="agency-dashboard">
      <MaliStripe />
      <SovereigntyBanner />
      <div className="bg-[#0b0f14] border-b border-zinc-800 px-4 sm:px-5 py-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-zinc-400 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>{ag.emergency_type}</span>
            <span className="text-zinc-700">·</span>
            <span className="text-zinc-500 font-mono normal-case tracking-normal">{ag.email}</span>
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight leading-none mt-1.5 text-zinc-100 truncate" data-testid="agency-name">
            {ag.name || t.agencyDashboard}
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            data-testid="agency-ws-indicator"
            title={wsLive ? "Flux temps réel actif" : wsConnecting ? "Reconnexion en cours" : "Flux temps réel hors ligne"}
            className={`hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-[0.12em] border ${
              wsLive
                ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
                : wsConnecting
                  ? "border-amber-500/40 text-amber-300 bg-amber-500/5 animate-pulse"
                  : "border-rose-500/40 text-rose-300 bg-rose-500/5"
            }`}
          >
            {wsLive
              ? <Wifi className="w-3 h-3" />
              : <WifiOff className="w-3 h-3 opacity-70" />}
            {wsLive ? "Direct" : wsConnecting ? "Reconnexion…" : "Hors-ligne"}
          </span>
          <button
            type="button"
            onClick={toggleSound}
            data-testid="agency-sound-toggle"
            aria-label={soundOn ? "Désactiver le son" : "Activer le son"}
            title={soundOn ? "Son activé — cliquez pour couper" : "Son coupé — cliquez pour activer"}
            className={`relative border px-2 py-1.5 rounded text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors flex items-center gap-1.5 ${
              soundOn && audioReady
                ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
                : !audioReady && soundOn
                  ? "border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                  : "border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
            }`}
          >
            {soundOn && audioReady ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
            {!audioReady && soundOn && (
              <span className="hidden sm:inline">Activer</span>
            )}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="agency-logout-btn"
            className="border border-zinc-700 text-zinc-200 px-3 py-1.5 rounded text-[10px] font-semibold uppercase tracking-[0.12em] hover:border-emerald-500/60 hover:text-emerald-300 transition-colors flex items-center gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" /> {t.agencyLogout}
          </button>
        </div>
      </div>

      {newCount > 0 && (
        <button
          type="button"
          onClick={clearNewBadge}
          data-testid="agency-new-alerts-banner"
          className="bg-amber-500/10 border-b border-amber-500/40 text-amber-200 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] flex items-center justify-between"
        >
          <span className="flex items-center gap-2">
            <Bell className="w-3.5 h-3.5" />
            {newCount === 1 ? "1 nouvelle alerte" : `${newCount} nouvelles alertes`}
          </span>
          <span className="text-[10px] text-amber-300/70 normal-case tracking-normal">Effacer</span>
        </button>
      )}

      <div className="flex border-b border-zinc-800 bg-[#0b0f14]">
        <button
          type="button"
          data-testid="agency-filter-open"
          onClick={() => setFilter("open")}
          className={`flex-1 px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors relative ${
            filter === "open" ? "text-emerald-300" : "text-zinc-400 hover:text-zinc-100"
          }`}
        >
          En cours ({openCount})
          {filter === "open" && (
            <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-emerald-400" aria-hidden />
          )}
        </button>
        <button
          type="button"
          data-testid="agency-filter-all"
          onClick={() => setFilter("all")}
          className={`flex-1 px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors relative ${
            filter === "all" ? "text-emerald-300" : "text-zinc-400 hover:text-zinc-100"
          }`}
        >
          Toutes ({alerts.length})
          {filter === "all" && (
            <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-emerald-400" aria-hidden />
          )}
        </button>
        <button
          type="button"
          onClick={manualRefresh}
          disabled={refreshing}
          data-testid="agency-refresh-btn"
          title={
            refreshing
              ? "Actualisation…"
              : refreshState.at
                ? (refreshState.ok ? `Mis à jour à ${fmtHM(refreshState.at)}` : "Échec — réessayer")
                : "Actualiser les alertes assignées"
          }
          className={`px-3 py-3 text-[10px] font-semibold uppercase tracking-[0.12em] flex items-center gap-1.5 transition-colors disabled:opacity-60 ${
            refreshState.ok === false
              ? "text-rose-300 hover:text-rose-200"
              : "text-zinc-400 hover:text-emerald-300"
          }`}
          aria-label="Actualiser"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing
            ? <span className="hidden sm:inline">Actualisation…</span>
            : refreshState.at
              ? (refreshState.ok
                  ? <span className="hidden sm:inline" data-testid="agency-refresh-ok">Mis à jour à {fmtHM(refreshState.at)}</span>
                  : <span className="hidden sm:inline" data-testid="agency-refresh-err">Échec</span>)
              : <span className="hidden sm:inline">Actualiser</span>}
        </button>
      </div>

      <div className="px-5 py-4 pb-8 space-y-2 bg-[#0a0d12]" data-testid="agency-alerts-list">
        <p className="text-[11px] text-zinc-500 mb-1">{t.agencyAlertsHere}</p>
        {loading && <div className="text-xs text-zinc-500">Chargement…</div>}
        {!loading && visible.length === 0 && (
          <div className="text-xs text-zinc-500 py-6 text-center">{t.agencyNoAlerts}</div>
        )}
        {visible.map((a) => (
          <AlertCard
            key={a.id}
            alert={a}
            caseFileRefreshKey={caseFileRefresh[a.id] || 0}
            onUpdate={(updated) => setAlerts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
          />
        ))}
      </div>
    </div>
  );
}
